import React, { useEffect, useState } from "react";
import moment from "moment";
import { validateDeposit, validatePrice } from "../../../Utils/Function";
import { isDepositEnabled } from "../../../config/environment.config";
import { isVerifiedTrainer } from "../../../Utils/VerifiedTrainerAmounts";
import { DV2 } from "../theme";
import { euros, studentFirstName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import { declarationHours, declarationPeriod, planningSummary } from "../summary";
import WizardShell from "../layout/WizardShell";
import PlanningAside from "../layout/PlanningAside";
import StudentMonthPanel from "../layout/StudentMonthPanel";
import { Card, FieldError, FieldLabel, Hint, Notice, TextLink, fieldStyle, useDv2Theme } from "../layout/Ui";
import SubjectSelect from "../components/SubjectSelect";
import DepositPanel, { DepositErrors } from "../components/DepositPanel";
import { AmountsBlock, CeilingPanel, scopeLabel } from "../components/AmountsBlock";
import { computeAmounts, depositCeiling, monthlyCeiling } from "../components/amounts";
import { isRelationTeachrChild, relationCeiling, relationRemuneration } from "../components/relationTeachr";
import { RelationRateField, RelationRemunerationBlock } from "../components/RelationRemunerationBlock";

/** Verified Trainer : pas de matière, identifiant fixe côté API (comme ThirdStep). */
const VT_SUBJECT_ID = 108;

/**
 * Étape 3 (maquettes 1d, 1k, 2d, 2k) : matière, prix de l'heure, acompte
 * éventuel, bloc de montants unique et plafond du mois. Règles copiées de
 * ThirdStep : prix entre 6 € et 138 € de l'heure, total facturé ≤ 990 €,
 * plafond mensuel, acompte ≤ moitié du montant facturé, date d'acompte après
 * la validation du compte, attestation certifiée et preuve obligatoire.
 *
 * Client en relation Teach'r (tarif fixé par la plateforme, voir
 * `relationTeachr.ts`) : ni champ de prix ni bloc de montants, seulement le
 * tarif horaire du prof et sa rémunération totale ; le plafond du mois est
 * contrôlé sans afficher de montant.
 */
const PricingStep = () => {
    const { teachr, state, update, goTo } = useDeclarationV2();
    const { fontFamily } = useDv2Theme();
    const student = state.student!;
    const child = student.child;
    const { pricing } = state;
    const isVt = isVerifiedTrainer();
    const isTeachrContact = !!child?.isTeachrContact;
    const relation = isRelationTeachrChild(child);
    const depositAllowed = isTeachrContact && isDepositEnabled() && !(teachr as any)?.scoop;
    const hours = declarationHours(state);
    const sessionsCount = state.courseType === "multiple" ? state.sessions.length : 1;
    const { start } = declarationPeriod(state);
    const isCurrentMonth = !!start && moment(start).format("YYYY-MM") === moment().format("YYYY-MM");

    const [errors, setErrors] = useState<{ subject?: string; price?: string } & DepositErrors>({});

    // Prix hors contact Teach'r : fixé par la fiche de l'élève (comme ThirdStep).
    useEffect(() => {
        if (!isTeachrContact && child?.pricePerHour && pricing.price !== String(child.pricePerHour)) {
            update({ pricing: { ...pricing, price: String(child.pricePerHour) } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTeachrContact, child?.pricePerHour]);

    const amounts = computeAmounts(pricing.price, hours, teachr, child, pricing.deposit.enabled ? pricing.deposit.amount : 0);
    const remuneration = relationRemuneration(child, hours);
    const ceiling = relation
        ? relationCeiling(child, hours, child?.amountForTheMonth, isCurrentMonth)
        : monthlyCeiling(pricing.price, hours, teachr, child, isCurrentMonth);
    const depositMax = depositCeiling(pricing.price, hours, teachr);
    const lastPrice = child?.pricePerHour ? parseFloat(child.pricePerHour) : null;

    const setPricing = (patch: Partial<typeof pricing>) => {
        setErrors({});
        update({ pricing: { ...pricing, ...patch } });
    };

    const validate = (): boolean => {
        const next: typeof errors = {};
        if (!isVt && !pricing.subject) next.subject = `Choisissez ${vocab.subjectLabelLower() === "matière" ? "la matière" : "la prestation"} ${vocab.ofTheCourse()}.`;
        const priceCheck = validatePrice(pricing.price, hours);
        if (isTeachrContact && !priceCheck.isValid) next.price = priceCheck.message;
        if (ceiling.exceeds) {
            next.price = relation
                ? "Plafond mensuel de l'avance immédiate dépassé pour cet élève. Retirez des séances ou déplacez-les sur le mois suivant."
                : `Total du mois (${euros(ceiling.total, 0)}) au-dessus du plafond de ${euros(ceiling.ceiling)}. Baissez le prix ou déplacez des séances sur le mois suivant.`;
        }
        if (depositAllowed && pricing.deposit.enabled) {
            const amount = parseFloat(pricing.deposit.amount);
            const depositCheck = validateDeposit(amount, depositMax, true);
            if (!depositCheck.isValid) next.amount = depositCheck.message;
            if (!pricing.deposit.date) next.date = "Indiquez la date de réception de l'acompte.";
            else if (student.activationDate && moment(pricing.deposit.date).format("YYYY-MM-DD") < moment(student.activationDate).format("YYYY-MM-DD")) {
                next.date = `La date d'acompte ne peut pas être antérieure à la validation du compte de ${vocab.yourClient()}.`;
            }
            if (!pricing.deposit.attestationAccepted) next.attestation = "Vous devez certifier cette attestation pour continuer.";
            if (!pricing.deposit.proof) next.proof = "Joignez la preuve du versement pour continuer.";
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const next = () => {
        if (!validate()) return;
        const subject = isVt ? { id: VT_SUBJECT_ID, fr_FR: "" } : pricing.subject;
        const deposit = depositAllowed && pricing.deposit.enabled ? pricing.deposit : { ...pricing.deposit, enabled: false };
        update({ pricing: { ...pricing, subject, deposit }, step: "summary" });
        goTo("summary");
    };

    const previous = () => goTo(state.courseType === "multiple" ? "sessions" : "planning");
    const firstName = studentFirstName(child);

    return (
        <WizardShell
            title={isVt ? "Votre tarif" : `${vocab.subjectLabel()} et tarif`}
            subtitle="Étape 3 sur 4"
            onBack={previous}
            asideMobile={<StudentMonthPanel child={child} teachr={teachr} compact />}
            aside={<PlanningAside current={3} planningDetail={planningSummary(state)} hideMonthPanel />}
            footer={{ onPrevious: previous, onNext: next }}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
                <div className="flex flex-col gap-4">
                    <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22, fontFamily }}>
                        {!isVt && (
                            <SubjectSelect teachrId={teachr?.id} value={pricing.subject} onChange={(subject) => setPricing({ subject })} error={errors.subject} />
                        )}
                        {relation ? (
                            <RelationRateField remuneration={remuneration} firstName={firstName} error={errors.price} />
                        ) : (
                        <div>
                            <FieldLabel htmlFor="dv2-price" hint={`par ${vocab.yourClient()}`}>Prix horaire payé</FieldLabel>
                            <div style={{ position: "relative" }}>
                                <input
                                    id="dv2-price"
                                    type="number"
                                    inputMode="decimal"
                                    min={6}
                                    max={138}
                                    step="0.5"
                                    value={pricing.price}
                                    disabled={!isTeachrContact}
                                    onChange={(e) => setPricing({ price: e.target.value })}
                                    placeholder="Ex. : 25"
                                    aria-invalid={!!errors.price}
                                    aria-describedby={errors.price ? "dv2-price-error" : "dv2-price-hint"}
                                    style={{ ...fieldStyle(!!errors.price), fontFamily, fontSize: 20, fontWeight: 800, paddingRight: 110 }}
                                />
                                <span aria-hidden style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: DV2.muted }}>€ par heure</span>
                            </div>
                            {errors.price ? <FieldError id="dv2-price-error">{errors.price}</FieldError> : (
                                <Hint>
                                    C'est ce que paie {vocab.yourClient()} pour une heure, avance immédiate déduite, pas ce que vous recevez.{" "}
                                    {!isTeachrContact
                                        ? `Tarif fixé avec ${firstName} dans sa fiche.`
                                        : lastPrice && String(lastPrice) === pricing.price
                                            ? `Même tarif que votre dernier ${vocab.course()} avec ${firstName}.`
                                            : lastPrice
                                                ? <>Dernier tarif avec {firstName} : {euros(lastPrice)} de l'heure. <TextLink label="Reprendre" size={12} onClick={() => setPricing({ price: String(lastPrice) })} /></>
                                                : null}
                                </Hint>
                            )}
                        </div>
                        )}
                    </Card>

                    {depositAllowed && (
                        <DepositPanel
                            teachr={teachr}
                            child={child}
                            value={pricing.deposit}
                            onChange={(deposit) => setPricing({ deposit })}
                            ceiling={depositMax}
                            minDate={student.activationDate ? moment(student.activationDate).format("YYYY-MM-DD") : null}
                            errors={errors}
                            multiple={state.courseType === "multiple"}
                        />
                    )}
                </div>

                <div className="flex flex-col gap-4">
                    {relation
                        ? <RelationRemunerationBlock remuneration={remuneration} scope={scopeLabel(sessionsCount)} />
                        : <AmountsBlock amounts={amounts} scope={scopeLabel(sessionsCount)} showDeposit={depositAllowed} />}
                    {isCurrentMonth && <CeilingPanel ceiling={ceiling} firstName={firstName} isVt={isVt} hideAmounts={relation} />}
                    {depositAllowed && pricing.deposit.enabled && (
                        <Card tint style={{ display: "flex", flexDirection: "column", gap: 6, padding: "18px 22px" }}>
                            <div style={{ fontWeight: 800, fontSize: 14 }}>Pourquoi une attestation et une preuve ?</div>
                            <div style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.5 }}>
                                L'URSSAF rembourse la moitié du prix total {vocab.ofTheCourse()}. Pour qu'un acompte soit pris en compte, elle demande votre attestation signée et une trace du paiement : virement, chèque ou paiement par carte bancaire.
                            </div>
                        </Card>
                    )}
                    {Object.keys(errors).length > 0 && (
                        <Notice tone="danger">Corrigez les champs signalés pour continuer.</Notice>
                    )}
                </div>
            </div>
        </WizardShell>
    );
};

export default PricingStep;
